<template>
  <AppHeader>Delivery Person Transaction</AppHeader>

  <Loading v-if="state === 'loading'" />

  <div v-if="state === 'idle'" class="p-4">
    <div class="p-3 text-gray-600 bg-white shadow rounded-2xl">
      <div>
        <p class="text-gray-600 text-lg">Purpose</p>
        <input type="text" v-model="entry.purpose" disabled class="w-full border rounded px-2 py-1 bg-gray-100 mt-1"/>
      </div>

      <div class="p-2 border rounded-xl bg-white mt-3 text-lg">
        <h2 class="text-xl font-semibold mb-2 text-gray-600">{{ deliveryPerson?.name }}<small> (#{{ deliveryPerson?.id }})</small>
          <span v-if="!hasBankDetail"><i class="ml-2 ion-md-information-circle text-yellow-500" title="Bank details missing"></i></span>
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <p class="block py-1">Trips</p>
            <p class="text-gray-700"><span class="font-semibold text-xl tracking-wide">{{ trips }}</span></p>
          </div>
          <div>
            <p class="block py-1">Salary / Day</p>
            <p class="text-gray-700 font-bold mt-1 text-xl tracking-wide">{{ toInr(deliveryPerson.salary_per_day) }}</p>
          </div>
          <div>
            <p class="block py-1">Payable Days</p>
            <input v-model.number="entry.payable_days" type="number" min="0" max="31" @input="updateEarningBasic" class="w-full text-xl border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.payable_days" class="text-red-500 text-sm mt-1">{{ errors.payable_days }}</p>
          </div>
          <div>
            <p class="block py-1">Basic +</p>
            <input v-model.number="entry.earning_basic" type="number" class="w-full text-xl border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.earning_basic" class="text-red-500 text-sm mt-1">{{ errors.earning_basic }}</p>
          </div>
          <div>
            <p class="block py-1">Allowance +</p>
            <input v-model.number="entry.earning_allowance" type="number" class="w-full text-xl border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.earning_allowance" class="text-red-500 text-sm mt-1">{{ errors.earning_allowance }}</p>
          </div>
          <div>
            <p class="block py-1">Other +</p>
            <input v-model.number="entry.earning_other" type="number" class="w-full text-xl border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.earning_other" class="text-red-500 text-sm mt-1">{{ errors.earning_other }}</p>
          </div>
          <div>
            <p class="block py-1">Late Logins <span>({{ deliveryPerson.late_logins }})</span> -</p>
            <input v-model.number="entry.deduction_late_login" type="number" class="w-full text-xl text-red-500 border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.deduction_late_login" class="text-red-500 text-sm mt-1">{{ errors.deduction_late_login }}</p>
          </div>
          <div>
            <p class="block py-1">Vehicle -</p>
            <input v-model.number="entry.deduction_vehicle" type="number" class="w-full text-xl text-red-500 border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.deduction_vehicle" class="text-red-500 text-sm mt-1">{{ errors.deduction_vehicle }}</p>
          </div>
          <div>
            <p class="block py-1">Advance -</p>
            <input v-model.number="entry.deduction_advance" type="number" class="w-full text-xl text-red-500 border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.deduction_advance" class="text-red-500 text-sm mt-1">{{ errors.deduction_advance }}</p>
          </div>
          <div>
            <p class="block py-1">Other -</p>
            <input v-model.number="entry.deduction_other" type="number" class="w-full text-xl text-red-500 border-2 border-slate-200 rounded-lg px-2 py-1 outline-none"/>
            <p v-if="errors.deduction_other" class="text-red-500 text-sm mt-1">{{ errors.deduction_other }}</p>
          </div>
          <div>
            <p class="block py-1">Payable</p>
            <input type="text" :value="toInr(calcPayable())" disabled class="w-full text-xl px-2 py-1 border-2 border-slate-200 rounded-lg tracking-wider bg-gray-100"/>
          </div>
        </div>
        <div class="col-span-2">
          <p class="block py-1">Notes</p>
          <textarea rows="2" v-model="entry.notes" class="w-full border-2 border-slate-200 rounded-lg px-2 py-1 outline-none" placeholder="Enter Notes"/>
        </div>
      </div>

      <div class="mt-5 text-center">
        <button @click="createSalary" class="px-3 py-2 text-sm leading-none text-gray-600 uppercase rounded-full bg-slate-300">Store Entries</button>
      </div>

    </div>
  </div>
</template>

<script>
import mixin from "@/Utils/mixin.js";
import formMixin from "@/utils/mixins/form.js";
import AppHeader from "@/components/common/Header.vue";
import Loading from "@/components/common/Loading.vue";

export default {
  name: "DeliveryPersonsTransaction",
  mixins: [mixin, formMixin],
  components: { AppHeader, Loading },

  data() {
    return {
      state: "idle",
      trips: 0,
      deliveryPerson: null,
      entry: {
        salary_per_day: 0,
        payable_days: 0,
        earning_basic: 0,
        earning_allowance: 0,
        deduction_late_login: 0,
        earning_other: 0,
        deduction_vehicle: 0,
        deduction_advance: 0,
        deduction_other: 0,
        notes: "",
      },
      errors: {},
    };
  },

  computed: {
    hasBankDetail() {
      if (!this.deliveryPerson) return false;
      const bank = this.deliveryPerson.bank;
      return bank && bank.bank && bank.ifsc && bank.name && bank.account && bank.verified === true;
    },
  },

  methods: {
    getData() {
      this.state = "loading";
      let id = this.$route.params.id;

      this.request("GET", "delivery-persons/" + id + "/transactions", {}, (resp) => {
        this.state = "idle";
        if (resp.success) {
          const data = resp.data;

          this.deliveryPerson = data.person;
          this.trips = data.person.trips_count_previous || 0;
          this.entry.purpose = data.person.purpose_label || "";
          this.entry.salary_per_day = Number(data.person.salary_per_day || 0);
          this.entry.earning_allowance = Number(data.person.salary?.allowance || 0);
          this.entry.deduction_late_login = Number(data.person.deduction_late_login || 0);
          this.entry.payable_days = this.trips;
          this.updateEarningBasic();
        }
      });
    },

    createSalary() {

      if (!this.hasBankDetail) {
        alert("Please update bank details and verify it.");
        return;
      }

      let id = this.$route.params.id;
      let rules = {
        payable_days: "required|numeric",
        earning_basic: "required|numeric",
        earning_allowance: "required|numeric",
        deduction_late_login: "required|numeric",
        earning_other: "required|numeric",
        deduction_vehicle: "required|numeric",
        deduction_advance: "required|numeric",
        deduction_other: "required|numeric",
      };

      if (!this.validateForm(this.entry, rules)) return;
      if (!confirm("Are you sure?")) return;

      this.state = "loading";
      this.request("POST", "delivery-persons/" + id + "/transactions", this.entry, (resp) => {
        this.state = "idle";
        alert(resp.message);
        if (resp.success) {
          this.$router.go(-1);
        }
      });
    },

    calcPayable() {
      const total =
        (this.entry.earning_basic || 0) +
        (this.entry.earning_allowance || 0) +
        (this.entry.earning_other || 0) -
        (this.entry.deduction_late_login || 0) -
        (this.entry.deduction_vehicle || 0) -
        (this.entry.deduction_advance || 0) -
        (this.entry.deduction_other || 0);

      return Math.round(total);
    },

    updateEarningBasic() {
      const days = this.entry.payable_days || 0;
      const perDay = this.entry.salary_per_day || 0;

      this.entry.earning_basic = Math.round(days * perDay);
    },
  },

  created() {
    this.getData();
  },
};
</script>
